
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Anchor, Settings, Package, Clipboard, Bot } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import NauticLogo from '@/components/ui/NauticLogo';
import Footer from '@/components/layout/Footer';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher';
import bannerAsset from '@/assets/nauticoach_banner.png.asset.json';

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('home');
  const { t: tc } = useTranslation('common');

  // Rediriger vers le dashboard si l'utilisateur est connecté
  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // SEO : titre et description selon la langue
  React.useEffect(() => {
    document.title = t('seo.title');
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', t('seo.description'));
  }, [t]);

  // Afficher un loader pendant le chargement de l'auth
  if (user === undefined) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nautic mx-auto mb-4"></div>
          <p className="text-nautic">{tc('states.loading')}</p>
        </div>
      </div>
    );
  }

  const checklistItems = [
    { tache: t('demo.check1'), done: true },
    { tache: t('demo.check2'), done: true },
    { tache: t('demo.check3'), done: true },
    { tache: t('demo.check4'), done: false },
    { tache: t('demo.check5'), done: false },
    { tache: t('demo.check6'), done: false },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Sélecteur de langue */}
      <div className="w-full bg-nautic">
        <div className="container mx-auto px-4 py-2 flex justify-end">
          <LanguageSwitcher variant="dark" />
        </div>
      </div>

      {/* Bannière d'accueil */}
      <div className="w-full">
        <img
          src={bannerAsset.url}
          alt="nauticoach.com"
          className="w-full h-auto object-contain"
        />
      </div>

      {/* Section principale */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to="/auth">
              <Button className="bg-nautic-gold hover:bg-nautic-lightgold text-nautic px-8 py-3 text-lg font-semibold">
                {t('cta.tryFree')}
              </Button>
            </Link>
            <Link to="/auth">
              <Button variant="outline" className="border-nautic-gold text-nautic hover:bg-nautic-gold hover:text-white px-8 py-3 text-lg font-semibold">
                {t('cta.login')}
              </Button>
            </Link>
          </div>

          {/* Encart promo AOUT2026 */}
          <Link to="/auth" className="block max-w-xl mx-auto mb-8">
            <div className="relative overflow-hidden bg-gradient-to-r from-nautic/5 via-nautic-gold/10 to-nautic/5 border-2 border-nautic-gold/40 rounded-2xl p-5 animate-[shimmer_3s_ease-in-out_infinite]">
              <div className="absolute inset-0 -translate-x-full animate-[shine_3s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-nautic-gold/20 to-transparent" />
              <div className="relative text-center space-y-2">
                <p className="text-sm font-bold text-nautic uppercase tracking-wide">{t('promo.badge')}</p>
                <p className="text-xl md:text-2xl font-bold text-nautic">{t('promo.headline')}</p>
                <div className="inline-flex items-center gap-2 bg-nautic-gold/20 border border-nautic-gold/40 rounded-full px-4 py-2">
                  <span className="text-sm text-nautic font-medium">{t('promo.codeLabel')}</span>
                  <span className="text-lg font-bold text-nautic tracking-wider">{t('promo.code')}</span>
                </div>
                <p className="text-xs text-muted-foreground font-medium">{t('promo.validity')}</p>
              </div>
            </div>
          </Link>

          <h1 className="text-3xl md:text-4xl font-bold text-nautic mb-4">
            {t('hero.title')}
          </h1>
          
          <p className="text-xl md:text-2xl font-semibold text-nautic-gold mb-3">
            {t('hero.subtitle')}
          </p>
          
          <p className="text-lg md:text-2xl font-bold text-nautic mb-4 max-w-2xl mx-auto">
            {t('hero.description')}
            <br />
            {t('hero.descriptionAi')}
          </p>

          <p className="text-xl md:text-2xl font-bold text-nautic mb-4">
            {t('hero.question')}
          </p>

          {/* Prix de lancement */}
          <p className="text-center text-lg md:text-xl font-bold text-nautic-gold mb-3">{t('pricing.unlock')}</p>
          <div className="relative overflow-hidden bg-gradient-to-r from-nautic/5 via-nautic-gold/10 to-nautic/5 border border-nautic-gold/30 rounded-2xl p-5 max-w-xl mx-auto mb-6 animate-[shimmer_3s_ease-in-out_infinite]">
            <div className="absolute inset-0 -translate-x-full animate-[shine_3s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-nautic-gold/20 to-transparent" />
            <p className="text-sm font-semibold text-nautic uppercase tracking-wide mb-2">{t('pricing.launchOffer')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
              <div className="text-center min-w-[140px]">
                <span className="text-2xl md:text-3xl font-bold text-nautic">4,99 €</span>
                <span className="text-muted-foreground text-sm">{t('pricing.perMonth')}</span>
                <span className="block text-xs font-bold text-transparent">&nbsp;</span>
              </div>
              <span className="hidden sm:block text-muted-foreground text-lg">{t('pricing.or')}</span>
              <span className="sm:hidden text-muted-foreground text-sm">{t('pricing.or')}</span>
              <div className="text-center min-w-[140px]">
                <span className="text-2xl md:text-3xl font-bold text-nautic">49,99 €</span>
                <span className="text-muted-foreground text-sm">{t('pricing.perYear')}</span>
                <span className="block text-xs font-bold text-nautic-gold">{t('pricing.twoMonthsFree')}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 justify-center mb-8">
            <span className="inline-flex items-center gap-1.5 bg-nautic/10 text-nautic px-4 py-2 rounded-full text-sm font-medium">
              {t('badges.free')}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-nautic-gold/15 text-nautic px-4 py-2 rounded-full text-sm font-medium">
              {t('badges.premium')}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-nautic/10 text-nautic px-4 py-2 rounded-full text-sm font-medium">
              {t('badges.passion')}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-nautic/10 text-nautic px-4 py-2 rounded-full text-sm font-medium">
              {t('badges.simple')}
            </span>
          </div>

          {/* Card décorative avec jauges */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
            {/* Card maintenance */}
            <Card className="border-nautic-gold/30 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <p className="text-sm font-semibold text-nautic mb-3">{t('demo.maintenanceTitle')}</p>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between bg-green-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-xs text-nautic">{t('demo.task1')}</span>
                    </div>
                    <span className="text-xs font-medium text-green-600">{t('demo.task1Status')}</span>
                  </div>
                  <div className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                      <span className="text-xs text-nautic">{t('demo.task2')}</span>
                    </div>
                    <span className="text-xs font-medium text-amber-600">{t('demo.task2Status')}</span>
                  </div>
                  <div className="flex items-center justify-between bg-red-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span className="text-xs text-nautic">{t('demo.task3')}</span>
                    </div>
                    <span className="text-xs font-medium text-red-600">{t('demo.task3Status')}</span>
                  </div>
                </div>
                <div className="mt-3 bg-nautic/5 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-1">{t('demo.costLabel')}</p>
                  <p className="text-2xl font-bold text-nautic-gold">1 250 €</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{t('demo.costHint')}</p>
                </div>
              </CardContent>
            </Card>

            {/* Card conversation assistant */}
            <Card className="border-nautic-gold/30 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <p className="text-sm font-semibold text-nautic mb-3">{t('demo.assistantTitle')}</p>
                <div className="space-y-2.5">
                  <div className="flex justify-end">
                    <div className="bg-nautic text-white text-xs px-3 py-2 rounded-2xl rounded-br-sm max-w-[85%]">
                      {t('demo.chat1')}
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-nautic-gold/15 text-nautic text-xs px-3 py-2 rounded-2xl rounded-bl-sm max-w-[85%]">
                      {t('demo.chat2')}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-nautic text-white text-xs px-3 py-2 rounded-2xl rounded-br-sm max-w-[85%]">
                      {t('demo.chat3')}
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-nautic-gold/15 text-nautic text-xs px-3 py-2 rounded-2xl rounded-bl-sm max-w-[85%]">
                      {t('demo.chat4')}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Deuxième rangée de cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
            {/* Card inventaire */}
            <Card className="border-nautic-gold/30 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <p className="text-sm font-semibold text-nautic mb-3">{t('demo.inventoryTitle')}</p>
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between rounded-lg px-3 py-2 bg-nautic/5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs">📡</span>
                      <span className="text-xs text-nautic font-medium">{t('demo.vhf')}</span>
                    </div>
                    <span className="text-xs font-medium text-green-600">1/1 ✅</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg px-3 py-2 bg-amber-50">
                    <div className="flex items-center gap-2">
                      <span className="text-xs">🥕</span>
                      <span className="text-xs text-nautic font-medium">{t('demo.carrots')}</span>
                    </div>
                    <span className="text-xs font-medium text-amber-600">2/4 ⚠️</span>
                  </div>
                </div>

                {/* Section avitaillement IA */}
                <div className="mt-3 border-t border-nautic-gold/20 pt-3">
                  <p className="text-xs font-semibold text-nautic mb-2">{t('demo.provisioningTitle')}</p>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center gap-1 bg-nautic/10 rounded-full px-2.5 py-1">
                      <span className="text-[10px]">👥</span>
                      <span className="text-[10px] font-medium text-nautic">{t('demo.people')}</span>
                    </div>
                    <div className="flex items-center gap-1 bg-nautic/10 rounded-full px-2.5 py-1">
                      <span className="text-[10px]">📅</span>
                      <span className="text-[10px] font-medium text-nautic">{t('demo.days')}</span>
                    </div>
                  </div>
                  <div className="bg-nautic-gold/10 rounded-lg p-2.5 space-y-1.5">
                    <div className="flex justify-between text-[11px]">
                      <span className="text-nautic">{t('demo.water')}</span>
                      <span className="font-medium text-nautic">{t('demo.waterQty')}</span>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-nautic">{t('demo.bread')}</span>
                      <span className="font-medium text-nautic">{t('demo.breadQty')}</span>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-nautic">{t('demo.pasta')}</span>
                      <span className="font-medium text-nautic">{t('demo.pastaQty')}</span>
                    </div>
                    <div className="flex justify-between text-[11px]">
                      <span className="text-nautic">{t('demo.meat')}</span>
                      <span className="font-medium text-nautic">{t('demo.meatQty')}</span>
                    </div>
                  </div>
                  <p className="text-[9px] text-muted-foreground text-center mt-1.5">{t('demo.provisioningHint')}</p>
                </div>
              </CardContent>
            </Card>

            {/* Card checklist */}
            <Card className="border-nautic-gold/30 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <p className="text-sm font-semibold text-nautic mb-3">{t('demo.checklistTitle')}</p>
                <div className="space-y-2">
                  {checklistItems.map((item, i) => (
                    <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${item.done ? 'bg-green-50' : 'bg-nautic/5'}`}>
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${item.done ? 'border-green-500 bg-green-500' : 'border-muted-foreground/30'}`}>
                        {item.done && <span className="text-white text-[10px]">✓</span>}
                      </div>
                      <span className={`text-xs ${item.done ? 'line-through text-muted-foreground' : 'text-nautic'}`}>{item.tache}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2">
                  <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                    <span>{t('demo.progress')}</span>
                    <span className="font-medium text-nautic">3/6</span>
                  </div>
                  <Progress value={50} className="h-2" />
                </div>
              </CardContent>
            </Card>
          </div>

        </div>

        {/* Fonctionnalités principales */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-nautic text-center mb-12">
            {t('features.title')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 max-w-7xl mx-auto">
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border">
              <div className="bg-nautic text-nautic-gold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Anchor className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-nautic mb-2">{t('features.boatsTitle')}</h3>
              <p className="text-gray-600 text-sm">{t('features.boatsText')}</p>
            </div>
            
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border">
              <div className="bg-nautic text-nautic-gold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Settings className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-nautic mb-2">{t('features.maintenanceTitle')}</h3>
              <p className="text-gray-600 text-sm">{t('features.maintenanceText')}</p>
            </div>
            
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border">
              <div className="bg-nautic text-nautic-gold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-nautic mb-2">{t('features.inventoryTitle')}</h3>
              <p className="text-gray-600 text-sm">{t('features.inventoryText')}</p>
            </div>
            
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border">
              <div className="bg-nautic text-nautic-gold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clipboard className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-nautic mb-2">{t('features.checklistsTitle')}</h3>
              <p className="text-gray-600 text-sm">{t('features.checklistsText')}</p>
            </div>

            <div className="text-center p-6 bg-white rounded-lg shadow-sm border">
              <div className="bg-nautic text-nautic-gold w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bot className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-semibold text-nautic mb-2">{t('features.assistantTitle')}</h3>
              <p className="text-gray-600 text-sm">{t('features.assistantText')}</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HomePage;
